import React from 'react';
import { useNavigate, useLocation, useOutlet } from 'react-router-dom';
import '../../css/Mypage/mypageList.css'

const STATUS_CLASS = {
  '-1': 'cancel',
  '0': 'reserved',
  '1': 'paid',
  '2': 'issuance',
  '3': 'onBoard',
  '5': 'external',
};

const STATUS_LABEL = {
  '-1': '취소 내역',
  '0': '예약 완료',
  '1': '부분 결제 완료',
  '2': '티켓 발행',
  '3': '승선 완료',
  '5': '연동 예약',
};

const BUTTON_LABEL = {
  '-1': '자세히 보기',
  '0': '자세히 보기',
  '1': '자세히 보기',
  '2': '자세히 보기',
  '3': '자세히 보기',
  '5': '자세히 보기',
};

const getStatusClass = (status) => STATUS_CLASS[String(status)] ?? String(status);
const getStatusLabel = (status, override) => {
  const key = String(status);
  return (override && override[key]) ?? STATUS_LABEL[key] ?? key;
};
const getButtonLabel = (status, override) => {
  const key = String(status);
  return (override && override[key]) ?? BUTTON_LABEL[key] ?? '자세히 보기';
};

const getDisplayStatus = (record, ticketIssueFilter) => {
  const statusValue = String(record?.status ?? '');
  if (ticketIssueFilter === 'on_board' && statusValue === '2') {
    return '3';
  }
  return statusValue;
};

const parseSailDate = (sailDateRaw) => {
  if (!sailDateRaw) return null;

  if (sailDateRaw instanceof Date && !Number.isNaN(sailDateRaw.getTime())) {
    return new Date(sailDateRaw.getTime());
  }

  let sailDate = new Date(sailDateRaw);
  if (Number.isNaN(sailDate.getTime()) && typeof sailDateRaw === 'string') {
    const normalized = sailDateRaw
      .replace(/년|월/g, '-')
      .replace(/일/g, '')
      .replace(/\./g, '-')
      .replace(/\s+/g, '');
    sailDate = new Date(normalized);
  }

  return Number.isNaN(sailDate.getTime()) ? null : sailDate;
};

const isBeforeSailDate = (record) => {
  const sailDate = parseSailDate(record?.reservSailDate);
  if (!sailDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  sailDate.setHours(0, 0, 0, 0);

  return today < sailDate;
};

const isCruiseEnded = (record) => {
  const endDate = parseSailDate(record?.reservEndDate) || parseSailDate(record?.reservSailDate);
  if (!endDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  // 종료일이 오늘보다 이전이면 과거 항해로 간주
  return endDate < today;
};

const getTicketIssueReady = (record) => {
  if (!record) return null;
  if (record.ticket_issue_ready === true) return true;
  if (record.ticket_issue_ready === false) return false;
  return null;
};

// status 2인 티켓 발행 항목은 출발일 이전 예약만 목록에 보여준다
export const shouldRenderRecord = (record, ticketIssueFilter) => {
  if (!record) return false;

  if (ticketIssueFilter === 'on_board') {
    const statusValue = String(record.status);
    if (statusValue === '3') return true;
    if (statusValue === '2') return isCruiseEnded(record);
    return false;
  }

  if (String(record.status) !== '2') return true;

  if (!isBeforeSailDate(record)) {
    return false;
  }

  const ticketReady = getTicketIssueReady(record);
  if (ticketIssueFilter === 'ready') {
    return ticketReady === true;
  }
  if (ticketIssueFilter === 'not_ready') {
    return ticketReady === false;
  }
  if (ticketReady === null) {
    return true;
  }
  return true;
};

function MypageList({ data, isLoading, error, statusLabelOverride = {}, buttonLabelOverride = {}, ticketIssueFilter }) {
  const navigate = useNavigate();
  const location = useLocation();
  const outlet = useOutlet();
  const renderedData = React.useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter((record) => shouldRenderRecord(record, ticketIssueFilter));
  }, [data, ticketIssueFilter]);
  const hasRenderableData = renderedData.length > 0;

  const handleDetailClick = (record) => {
    if (!record) return;

    const statusValue = getDisplayStatus(record, ticketIssueFilter);
    const reservationIdx = record.reservation_idx;
    const reservationSchIdx = record.schedule_idx;
    const reservationCode = record.reservation_code;

    const basePath = location.pathname.replace(/\/detail.*$/, '');
    navigate(
      basePath + `/detail?status=${statusValue}&idx=${reservationIdx}`,
      { state: { reservationSchIdx, reservationCode } }
    );
  };

  // 상세 페이지일 경우 Outlet만 렌더링
  if (outlet) {
    return outlet;
  }

  return (
    <>
      {isLoading ? (
        <div className="no-data-message">
          <span className="no-data-msg-title">목록을 불러오는 중입니다.</span>
        </div>
      ) : error ? (
        <div className="no-data-message">
          <span className="no-data-msg-title">목록을 불러올 수 없습니다.</span>
        </div>
      ) : hasRenderableData ? (
        renderedData.map((record, recordIdx) => {
          const displayStatus = getDisplayStatus(record, ticketIssueFilter);
          const statusClassName = getStatusClass(displayStatus);

          return (
          <div className='mypage-list-item' key={(record.reservation_idx ?? record.id ?? recordIdx) + recordIdx}>
            <div className='item-header'>
              <h3 className={`status ${statusClassName}`}>
                {getStatusLabel(displayStatus, statusLabelOverride)}
              </h3>
              <span style={{display:'none'}}>{record.status}-{record.reservation_idx}</span>
              {record.reservation_code ? (
                <p className='id'>예약번호<span className='id-value'>{record.reservation_code}</span></p>
              ) : null}
              <p className='name'>{record.reservSailName}</p>
            </div>
            
            <div className='item-body'>
              {record.img_src ? (
                <img
                  className='image'
                  src={record.img_src}
                  alt='크루즈 이미지'
                  onError={(event) => {
                    if (event?.currentTarget) {
                      event.currentTarget.style.display = 'none';
                    }
                  }}
                />
              ) : null}
              <div className='info'>
                {(() => {
                  const statusValue = String(record.status);
                  if (statusValue === '0') {
                    return null;
                  }
                  const formattedDate = (() => {
                    if (!record.create_date) return '미정';
                    const date = new Date(record.create_date);
                    if (Number.isNaN(date.getTime())) return record.create_date;
                    const year = date.getFullYear();
                    const month = String(date.getMonth() + 1).padStart(2, '0');
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${year}년 ${month}월 ${day}일`;
                  })();

                  return (
                    <p>예약일시<span className='value'>{formattedDate}</span></p>
                  );
                })()}
                <p>출발<span className='value'>{record.reservSailDate ? record.reservSailDate : '미정'}<span className='location'>{record.reservDepartPort ? record.reservDepartPort : '미정'}</span></span></p>
                <p>도착<span className='value'>{record.reservEndDate ? record.reservEndDate : '미정'}<span className='location'>{record.reservEndPort ? record.reservEndPort : '미정'}</span></span></p>
                <div className='headCount'>
                  인원
                  <div className='value-wrapper'>
                    {/* {record.passenger_name} {record.guest_amt} */}
                    {record.adult ? <span className='value'>성인 {record.adult}명</span> : null}
                    {record.child ? <span className='value'>청소년 {record.child}명</span> : null}
                  </div>
                </div>

                <p>선실<span className='value'>{record.cabin ? record.cabin : '미정'}</span></p>
              </div>
            </div>
            
            <button
              type='button'
              className={`btn ${statusClassName}`}
              onClick={() => handleDetailClick(record)}
            >
              {getButtonLabel(displayStatus, buttonLabelOverride)}
            </button>
          </div>
        )})
      ) : (
        <div className="no-data-message">
          <span className='no-data-msg-title'>내역이 없습니다.</span>
          <span className='no-data-msg-inner-title'>예약 내역은 마이페이지에서 확인할 수 있어요.</span>
        </div>
      )}
    </>
  );
}

export default MypageList;
